import { useEffect, useState } from "react";

import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import Input from "../../../components/Inputs/Input";
import Modal, { ModalBody, ModalFooter } from "../../../components/Modal/Modal";
import {
  addProduct,
  fetchInfoQuestions,
  fetchInfoQuestionsCount,
  getInfoQuestions,
  updateProduct,
} from "../../../services/masters";
import Product from "../../../ts/models/Products";
import classes from "./AddProduct.module.css";
import { InfoQuestionType } from "../../../ts/enums";
import InfoQuestion from "../../../ts/models/InfoQuestion";
import { Column } from "../../../ts/types";
import LinkButton from "../../../components/Buttons/LinkButton";
import SelectItemsModal from "../../../components/SelectItems/SelectItemsModal";
import InfoQuestionsOrdering from "./InfoQuestionsOrdering/InfoQuestionsOrdering";
import Table from "../../../components/CustTable/Table";
import InputLabel from "../../../components/Inputs/InputLabel";
import addIcon from "../../../assets/icons/add-circle.svg";
import { getClasses } from "../../../utils";

type Props = {
  product: Product;
  onClose: (isUpdated: boolean) => void;
};

const infoQuestionTypes = [
  {
    name: "Option List",
    type: InfoQuestionType.OptionList,
  },
  {
    name: "Free Text",
    type: InfoQuestionType.FreeText,
  },
  {
    name: "Number",
    type: InfoQuestionType.Number,
  },
  {
    name: "Date",
    type: InfoQuestionType.Date,
  },
];

const pageSize = 10;

const AddProduct: React.FC<Props> = ({ product, onClose }) => {
  const [data, setData] = useState(product);
  const isEdit = data.id;
  const [selectedInfoQuestionIds, setSelectedInfoQuestionIds] = useState<
    Set<string>
  >(new Set<string>(new Set(product.infoQuestions)));
  const [infoQuesTotalPages, setInfoQuesTotalPages] = useState(1);
  const [infoQuestions, setInfoQuestions] = useState<InfoQuestion[]>([]);
  const [selectedInfoQuestions, setSelectedInfoQuestions] = useState<
    InfoQuestion[]
  >([]);
  const [allSelectedInfoQuestions, setAllSelectedInfoQuestions] = useState<
    InfoQuestion[]
  >([]);
  const [showInfoQuestionsModal, setShowInfoQuestionsModal] = useState(false);
  const [showChangeOrderingModal, setShowChangeOrderingModal] = useState(false);

  const addProductHandler = async () => {
    const productData = {
      ...data,
      infoQuestions: Array.from(selectedInfoQuestionIds),
    };

    let res;
    try {
      if (isEdit) {
        res = await updateProduct(productData);
      } else {
        res = await addProduct(productData);
      }
      if (res.status === 200) {
        onClose(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const infoQuestionTableColumn: Column<InfoQuestion>[] = [
    {
      header: "ID",
      accessor: (e, idx) => idx! + 1,
      width: "10%",
    },
    {
      header: "CODE",
      accessor: (e) => e.code,
      width: "10%",
    },
    {
      header: "QUESTION",
      accessor: (e) => e.question,
      width: "45%",
    },
    {
      header: "TYPE",
      accessor: (e) =>
        infoQuestionTypes.find(
          (questionType) => questionType.type === e.questionType
        )?.name || "",
      width: "10%",
    },
    {
      header: "OPTIONS",
      accessor: (e) => (
        <div>
          {e.questionType !== InfoQuestionType.OptionList && (
            <div
              className={getClasses(
                classes.questionType,
                classes.notApplicable
              )}>
              NA
            </div>
          )}
          {e.options.map((item, idx) => (
            <div key={idx}>
              {`${idx + 1}. `}
              {item}
            </div>
          ))}
        </div>
      ),
      width: "20%",
    },
    {
      header: "ACTION",
      accessor: (e) => (
        <LinkButton onClick={() => removeInfoQuestion(e.id)} label='Remove' />
      ),
    },
  ];
  const removeInfoQuestion = (id: string) => {
    setSelectedInfoQuestionIds((state) => {
      const newInfoQuestionSet = new Set(state);
      newInfoQuestionSet.delete(id);
      return newInfoQuestionSet;
    });
  };

  const getQuestionById = (questionId?: string) => {
    return allSelectedInfoQuestions.find(
      (question) => question.id === questionId
    );
  };

  const selectInfoQuestionsColumns: Column<InfoQuestion>[] = [
    {
      header: "CODE",
      accessor: (e) => e.code,
      width: "7%",
    },
    {
      header: "QUESTION",
      accessor: (e) => e.question,
      width: "26%",
    },
    {
      header: "TYPE",
      accessor: (e) =>
        infoQuestionTypes.find(
          (questionType) => questionType.type === e.questionType
        )?.name || "",
      width: "8%",
    },
    {
      header: "OPTIONS",
      accessor: (e) => (
        <div>
          {e.questionType !== InfoQuestionType.OptionList && (
            <div
              className={getClasses(
                classes.questionType,
                classes.notApplicable
              )}>
              NA
            </div>
          )}
          {e.options.map((item, idx) => (
            <div key={idx}>
              {`${idx + 1}. `}
              {item}
            </div>
          ))}
        </div>
      ),
      width: "14%",
    },
    {
      header: "SHOWN WHEN",
      accessor: (e) => getQuestionById(e?.logicQuestion)?.question || "",
      width: "25%",
    },
    {
      header: "RESPONSE IS",
      accessor: (e) => e.logicQuestionResponse || "",
    },
  ];

  const searchInfoQuestionsHandler = async (
    offset: number = 0,
    searchText: string = ""
  ) => {
    const filter = {
      limit: 10,
      offset,
      where: {
        $or: [
          {
            question: {
              $regex: `.*${searchText}.*`,
              $options: "i",
            },
          },
        ],
      },
    };
    try {
      const [infoQuestionRes, countRes] = await Promise.all([
        fetchInfoQuestions(filter),
        fetchInfoQuestionsCount(filter.where),
      ]);
      if (infoQuestionRes?.status === 200) {
        setInfoQuestions(infoQuestionRes.data);
      }
      if (countRes?.status === 200) {
        const noOfPages = Math.ceil(Number(countRes.data.count) / pageSize);
        setInfoQuesTotalPages(noOfPages);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const fetchInfoQuestionsFromIds = async () => {
      const logicQuestions = infoQuestions.filter((data) => data.logicQuestion);
      const infoQuestionIds = logicQuestions.map(
        (infoQues) => infoQues.logicQuestion
      );

      if (infoQuestionIds?.length) {
        try {
          const infoQuestionRes = await getInfoQuestions(infoQuestionIds);
          if (infoQuestionRes.status) {
            setAllSelectedInfoQuestions(infoQuestionRes.data);
          }
        } catch (err) {
          console.log(err);
        }
      } else {
        setAllSelectedInfoQuestions([]);
      }
    };

    if (infoQuestions?.length > 0) {
      fetchInfoQuestionsFromIds();
    }
  }, [infoQuestions?.length]);

  useEffect(() => {
    const fetchInfoQuestionsFromIds = async () => {
      const infoQuestionIds = Array.from(selectedInfoQuestionIds);
      if (infoQuestionIds?.length) {
        try {
          const infoQuestionRes = await getInfoQuestions(infoQuestionIds);
          if (infoQuestionRes.status) {
            let infoQuestion: any[] = [];
            let infoQuestionMap = new Map<string, InfoQuestion>();
            infoQuestionRes.data.forEach((infoQuestion: InfoQuestion) => {
              infoQuestionMap.set(infoQuestion.id, infoQuestion);
            });
            infoQuestion = infoQuestionIds.map((questionId) =>
              infoQuestionMap.get(questionId)
            );
            setSelectedInfoQuestions(infoQuestion);
          }
        } catch (err) {
          console.log(err);
        }
      } else {
        setSelectedInfoQuestions([]);
      }
    };

    fetchInfoQuestionsFromIds();
  }, [selectedInfoQuestionIds]);

  const isRequiredDataMissing = () => {
    if (!data.code?.trim() || !data.description?.trim() || !data.name?.trim()) {
      return true;
    }
    return false;
  };

  return (
    <>
      {showInfoQuestionsModal && (
        <SelectItemsModal<InfoQuestion>
          title={"Add Info Questions"}
          initialItemsSet={selectedInfoQuestionIds}
          setItemsHandler={(items) => setSelectedInfoQuestionIds(items)}
          onClose={() => setShowInfoQuestionsModal(false)}
          items={infoQuestions}
          noOfPages={infoQuesTotalPages}
          columns={selectInfoQuestionsColumns}
          searchItemsHandler={searchInfoQuestionsHandler}
          isPaginated
          style={{ width: "80vw" }}
        />
      )}
      {showChangeOrderingModal && (
        <InfoQuestionsOrdering
          items={selectedInfoQuestions}
          onClose={() => setShowChangeOrderingModal(false)}
          setItemsHandler={(items) => setSelectedInfoQuestionIds(items)}
        />
      )}
      <Modal
        className={classes.heading}
        title={isEdit ? <>Update Product</> : <>Add Product</>}
        onClose={() => onClose(false)}
        style={{ height: "85%" }}>
        <>
          <ModalBody>
            <div className={classes.main}>
              <div className={classes.code}>
                <Input
                  name='Code'
                  value={data.code}
                  onChange={(e) =>
                    setData((state) => ({ ...state, code: e.target.value }))
                  }
                  className={classes.input}
                />
                <Input
                  name='CPT Code'
                  value={data.cptCode}
                  onChange={(e) =>
                    setData((state) => ({ ...state, cptCode: e.target.value }))
                  }
                  className={classes.input}
                />
                <Input
                  name='Name'
                  value={data.name}
                  onChange={(e) =>
                    setData((state) => ({
                      ...state,
                      name: e.target.value,
                    }))
                  }
                  className={classes.input}
                />
              </div>
              <Input
                name='Description'
                value={data.description}
                onChange={(e) =>
                  setData((state) => ({
                    ...state,
                    description: e.target.value,
                  }))
                }
                textarea
                className={classes.input}
              />
              <div className={classes.products}>
                <InputLabel label='Info Questions' />
                <Table
                  columns={infoQuestionTableColumn}
                  data={selectedInfoQuestions}
                />
                <div className={classes.selectItemContainer}>
                  <LinkButton
                    label='Change Ordering'
                    onClick={() => setShowChangeOrderingModal(true)}
                  />
                  <LinkButton
                    icon={<img src={addIcon} alt='' />}
                    label='Select Info Questions'
                    onClick={() => setShowInfoQuestionsModal(true)}
                  />
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <div className={classes.actions}>
              <PrimaryButton
                className={classes.actionBtn}
                name={isEdit ? "Update" : "Add"}
                onClick={addProductHandler}
                style={{ marginRight: "1rem" }}
                disabled={isRequiredDataMissing()}
              />
              <PrimaryButton
                className={classes.actionBtn}
                name='Cancel'
                onClick={() => onClose(false)}
                transparent
              />
            </div>
          </ModalFooter>
        </>
      </Modal>
    </>
  );
};

export default AddProduct;
